import React from "react";
import { Link } from "react-router-dom";
import { siteConfig } from "../Datas/productData";
import "../css/terms.scss";

const termsSections = [
  ["scope", "Scope and acceptance"],
  ["products", "Products and availability"],
  ["orders", "Orders and acceptance"],
  ["prices", "Prices and payment"],
  ["delivery", "Delivery and ownership"],
  ["returns", "Returns and cancellations"],
  ["use", "Responsible site use"],
  ["intellectual-property", "Intellectual property"],
  ["liability", "Liability"],
  ["general", "General terms and contact"],
];

function Terms() {
  const { brandName, contact } = siteConfig;

  return (
    <main className="policy-page terms" aria-labelledby="terms-title">
      <header className="policy-page__header">
        <p className="policy-page__eyebrow">Legal / Terms</p>
        <h1 id="terms-title">Terms of service</h1>
        <div className="policy-page__summary">
          <p>
            These terms govern use of the {brandName} website and purchases
            made through our online store. Please read them before ordering.
          </p>
          <time dateTime="2026-08-14">Last updated 14 August 2026</time>
        </div>
      </header>

      <div className="policy-page__layout">
        <nav className="policy-page__contents" aria-label="Terms of service contents">
          <p>On this page</p>
          <ol>
            {termsSections.map(([id, label], index) => (
              <li key={id}>
                <a href={`#${id}`}>
                  <span>{String(index + 1).padStart(2, "0")}</span>
                  {label}
                </a>
              </li>
            ))}
          </ol>
        </nav>

        <article className="policy-page__content">
          <section id="scope">
            <h2>Scope and acceptance</h2>
            <p>
              By visiting the site or placing an order, you agree to these
              terms and our Privacy Policy. You must have legal capacity to
              enter a contract and provide accurate, current information.
            </p>
            <p>
              We may revise these terms from time to time. The version available
              when an order is placed applies to that order. If you do not agree
              with the terms, please do not use the store.
            </p>
          </section>

          <section id="products">
            <h2>Products and availability</h2>
            <p>
              We take care to describe and photograph products accurately.
              Colours can vary between screens, and measurements are
              approximate. Natural materials and hand-finished surfaces may
              show small variations that are part of the piece rather than a
              defect.
            </p>
            <p>
              Products remain subject to availability. Adding a piece to the
              cart does not reserve it. We may limit quantities or withdraw a
              product when reasonably necessary.
            </p>
          </section>

          <section id="orders">
            <h2>Orders and acceptance</h2>
            <p>
              After checkout, you will receive an acknowledgement at the email
              address supplied. This confirms receipt of your request, not final
              acceptance. A binding sale is formed when we confirm dispatch or
              otherwise expressly accept the order.
            </p>
            <p>
              We may decline or cancel an order before acceptance because of
              unavailable stock, payment authorisation, suspected fraud, an
              obvious pricing error or delivery restrictions. Any captured
              payment for a cancelled item will be returned.
            </p>
          </section>

          <section id="prices">
            <h2>Prices and payment</h2>
            <p>
              Prices are shown in the selected currency. Applicable taxes are
              included or displayed at checkout according to destination.
              Delivery charges and any duties not included in the product price
              are shown before you confirm the order where we can calculate
              them.
            </p>
            <p>
              Payment must be made using an offered method. Payment providers
              may perform additional authentication. If an obvious pricing or
              description error occurs, we will contact you before proceeding.
            </p>
          </section>

          <section id="delivery">
            <h2>Delivery and ownership</h2>
            <p>
              Delivery estimates are indicative and begin after order
              processing. Delays may occur due to carrier, customs or events
              outside reasonable control. Please inspect the address carefully:
              changes are not always possible after dispatch.
            </p>
            <p>
              Risk passes when the order is delivered to you or a person you
              nominate. Ownership passes once full payment has been received.
              Further details appear in our <Link to="/shipping">Delivery policy</Link>.
            </p>
          </section>

          <section id="returns">
            <h2>Returns and cancellations</h2>
            <p>
              Eligible purchases may be returned within the period and in the
              condition described in our <Link to="/returns">Returns policy</Link>.
              Statutory cancellation or consumer rights in your country remain
              unaffected.
            </p>
          </section>

          <section id="use">
            <h2>Responsible site use</h2>
            <p>You agree not to:</p>
            <ul>
              <li>use the site for unlawful, fraudulent or harmful activity;</li>
              <li>interfere with security, availability or another user;</li>
              <li>introduce malicious code or attempt unauthorised access;</li>
              <li>scrape or reproduce the store at scale without permission; or</li>
              <li>use false details or impersonate another person.</li>
            </ul>
            <p>Access may be restricted where necessary to protect the store or others.</p>
          </section>

          <section id="intellectual-property">
            <h2>Intellectual property</h2>
            <p>
              The site, its design, copy, photography, product designs,
              graphics and other materials are owned by or licensed to
              {` ${brandName}`}. They may be viewed for personal shopping use,
              but may not be copied, adapted, sold or commercially exploited
              without written permission.
            </p>
          </section>

          <section id="liability">
            <h2>Liability</h2>
            <p>
              Nothing in these terms excludes liability that cannot legally be
              excluded, or limits mandatory consumer rights. To the fullest
              extent permitted by law, we are not responsible for indirect or
              unforeseeable loss, business loss, or loss caused by misuse,
              third-party services or circumstances outside reasonable control.
            </p>
          </section>

          <section id="general">
            <h2>General terms and contact</h2>
            <p>
              If one provision is unenforceable, the remaining provisions stay
              effective. A delay in enforcing a right is not a waiver. These
              terms are governed by applicable law, without depriving consumers
              of mandatory protections in their place of residence.
            </p>
            <div className="policy-page__callout">
              <p>Questions about these terms</p>
              <a href={`mailto:${contact.email}`}>{contact.email}</a>
              <span>{contact.address}</span>
              <Link to="/contact">Open the contact form ↗</Link>
            </div>
          </section>
        </article>
      </div>
    </main>
  );
}

export default Terms;
